import { CurrencyFormatter, type ProductClientPublicFetch } from "@sticker-giant/sg-frontend-common";
import { useQuery } from "@tanstack/react-query";
import { buildStartingPriceRequest } from "@/lib/product/pricing";
import type { PricingClientPublic } from "@/services/PricingClientPublic";
import { useServicesStore } from "@/stores/useServicesStore";
export const getPriceLabelsQueryKey = (skus: string[]) => ["price-labels", ...skus.slice().sort()];
async function fetchPriceLabelForSku(sku: string, productClient: ProductClientPublicFetch, pricingClient: PricingClientPublic): Promise<string | null> {
  try {
    const product = await productClient.getProductBySku({
      sku,
      includeChildren: false,
      fetchRatingSummary: false
    });
    if (!product) return null;
    const request = buildStartingPriceRequest(product);
    const pricing = await pricingClient.getPricingForItem(request);
    const first = pricing?.quantityPrices[0];
    if (!first) return null;
    const price = Number(first.price);
    if (Number.isNaN(price)) return null;
    return `${first.quantity} at ${CurrencyFormatter.formatUSD(price)}`;
  } catch (error) {
    console.error("Failed to fetch price label for SKU:", sku, error);
    return null;
  }
}
export const usePriceLabels = (skus: string[], enabled = true) => {
  const {
    productClientPublic,
    pricingClientPublic
  } = useServicesStore.getState();
  return useQuery<Record<string, string | null>>({
    queryKey: getPriceLabelsQueryKey(skus),
    enabled: enabled && Boolean(productClientPublic) && Boolean(pricingClientPublic) && skus.length > 0,
    staleTime: 60_000,
    queryFn: async () => {
      if (!productClientPublic || !pricingClientPublic) {
        throw new Error("Clients not available");
      }
      const labels = await Promise.all(skus.map(sku => fetchPriceLabelForSku(sku, productClientPublic, pricingClientPublic)));
      const results: Record<string, string | null> = {};
      for (let i = 0; i < skus.length; i++) {
        results[skus[i]] = labels[i];
      }
      return results;
    }
  });
};